// SprayOriginIPWatchlistManager — page at /settings/spray-origin-watchlist (v1.102.0).
//
// Review & revoke UI over the PIPELINE-WRITTEN `SprayOriginIPWatchlist` table. The
// detection writer auto-populates attacker originating IP prefixes from Entra ID
// Protection password-spray alerts; the hot-tier rule `rule_spray_success_signin`
// then fires HIGH when a *successful* sign-in comes from a listed prefix. The list
// is global (an IP that sprayed any tenant protects all tenants), so a false-positive
// auto-add (shared VPN egress, CGNAT/carrier range, over-wide IPv6 supernet) can
// produce HIGH alerts on legitimate sign-ins.
//
// This page lets an analyst REVIEW recent entries (with provenance to judge a FP) and
// REVOKE (soft-disable) one so the streaming rule stops firing on it within ~30s —
// without a hard delete (the row is kept for audit) and without a pipeline deploy.
// Read-open; revoke/re-enable is global-admin-only. The portal owns ONLY the `enabled`
// flag + revoke audit fields; it never adds rows. Every write bumps a __version
// sentinel the pipeline polls (~30s).

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  MagnifyingGlassIcon,
  SignalIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
  LockClosedIcon,
  ArrowsRightLeftIcon,
  ArrowTopRightOnSquareIcon,
  NoSymbolIcon,
  ArrowUturnLeftIcon,
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import {
  fetchSprayOriginWatchlist,
  fetchSprayOriginWatchlistSentinelHealth,
  updateSprayOriginWatchlistEntry,
  fetchSprayOriginMatchingAlerts,
  reconcileSprayOriginWatchlistSentinel,
} from '../api/client';
import { useAuth } from '../hooks/useAuth';
import Badge from './ui/Badge';
import { severityTone } from './ui/badgeTones';
import Pagination from './Pagination';
import { RefreshButton, RefreshingBanner } from './ui/LoadingState';

function formatDate(dateStr) {
  if (!dateStr) return '—';
  try { return format(parseISO(dateStr), 'MMM d, yyyy'); } catch { return dateStr; }
}
function formatDateTime(dateStr) {
  if (!dateStr) return '—';
  try { return format(parseISO(dateStr), 'MMM d, yyyy HH:mm'); } catch { return dateStr; }
}
function ipVersionLabel(v) {
  const s = String(v || '');
  if (s === '6') return 'IPv6';
  if (s === '4') return 'IPv4';
  return s ? `IPv${s}` : '—';
}

export default function SprayOriginIPWatchlistManager() {
  const { user } = useAuth();
  const isAdmin = !!user?.isAdmin;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  const [filterQuery, setFilterQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all | enabled | disabled
  const [minTenants, setMinTenants] = useState('');

  const [health, setHealth] = useState(null);
  const [reconciling, setReconciling] = useState(false);
  const [confirmReconcile, setConfirmReconcile] = useState(false);

  const [sortKey, setSortKey] = useState('lastSeenUtc');
  const [sortDir, setSortDir] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [expandedKey, setExpandedKey] = useState(null);
  const [matchCache, setMatchCache] = useState({});   // rowKey -> { loading, error, data }
  const [revokeTarget, setRevokeTarget] = useState(null); // row being revoked
  const [revokeReason, setRevokeReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [togglingKey, setTogglingKey] = useState(null);

  // Resizable columns: chevron, Prefix, Ver, Tenants, Sprays, First seen, Last seen, Risk, Enabled
  const [colWidths, setColWidths] = useState([40, 220, 80, 260, 90, 130, 130, 90, 110]);
  const thRefs = useRef([]);
  const tableRef = useRef(null);

  const startResize = useCallback((e, colIdx) => {
    e.preventDefault();
    const th = thRefs.current[colIdx];
    const table = tableRef.current;
    if (!th) return;
    const startX = e.clientX;
    const startWidth = th.offsetWidth;
    const startTableWidth = table ? table.offsetWidth : 0;
    let lastWidth = startWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const onMouseMove = (moveEvt) => {
      lastWidth = Math.max(40, startWidth + moveEvt.clientX - startX);
      th.style.width = `${lastWidth}px`;
      if (table) table.style.width = `${startTableWidth + (lastWidth - startWidth)}px`;
    };
    const onMouseUp = () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      setColWidths(prev => { const n = [...prev]; n[colIdx] = lastWidth; return n; });
      if (table) table.style.width = '';
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, []);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchSprayOriginWatchlist();
      setRows(Array.isArray(data?.entries) ? data.entries : []);
      if (data?.sentinel) setHealth(data.sentinel);
    } catch (err) {
      setError(err.message || 'Failed to load spray-origin watchlist');
      setRows([]);
    } finally {
      setLoading(false);
      setHasLoaded(true);
    }
  };

  const loadHealth = async () => {
    try { setHealth(await fetchSprayOriginWatchlistSentinelHealth()); } catch { /* best-effort */ }
  };

  useEffect(() => {
    load();
    const intervalId = setInterval(loadHealth, 30_000);
    return () => clearInterval(intervalId);
  }, []);

  const filtered = useMemo(() => {
    let r = rows;
    if (statusFilter === 'enabled') r = r.filter((x) => x.enabled !== false);
    else if (statusFilter === 'disabled') r = r.filter((x) => x.enabled === false);
    const minT = parseInt(minTenants, 10);
    if (!Number.isNaN(minT)) r = r.filter((x) => (x.sprayedTenantCount || 0) >= minT);
    if (filterQuery.trim()) {
      const q = filterQuery.toLowerCase();
      r = r.filter((x) =>
        (x.prefixCidr && x.prefixCidr.toLowerCase().includes(q)) ||
        (x.exactIpsSeen && x.exactIpsSeen.toLowerCase().includes(q)) ||
        (x.sourceFeed && x.sourceFeed.toLowerCase().includes(q)) ||
        (Array.isArray(x.sprayedTenants) && x.sprayedTenants.some((t) =>
          (t.friendlyName && t.friendlyName.toLowerCase().includes(q)) ||
          (t.tenantId && t.tenantId.toLowerCase().includes(q))
        ))
      );
    }
    return r;
  }, [rows, statusFilter, minTenants, filterQuery]);

  const sorted = useMemo(() => {
    const result = [...filtered];
    result.sort((a, b) => {
      const av = a[sortKey] ?? '';
      const bv = b[sortKey] ?? '';
      if (av === bv) return 0;
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      return sortDir === 'asc' ? 1 : -1;
    });
    return result;
  }, [filtered, sortKey, sortDir]);

  useEffect(() => { setCurrentPage(1); }, [filterQuery, statusFilter, minTenants, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIdx = (safeCurrentPage - 1) * pageSize;
  const paged = sorted.slice(startIdx, startIdx + pageSize);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir(key === 'lastSeenUtc' || key === 'firstSeenUtc' || key === 'sprayCount' || key === 'sprayedTenantCount' ? 'desc' : 'asc'); }
  };

  const SortIndicator = ({ k }) => {
    if (sortKey !== k) return <span className="text-gray-700 ml-1">{'↕'}</span>;
    return sortDir === 'asc' ? <ChevronUpIcon className="inline h-3 w-3 ml-1" /> : <ChevronDownIcon className="inline h-3 w-3 ml-1" />;
  };

  const toggleExpand = (row) => {
    const key = row.rowKey;
    setExpandedKey((prev) => (prev === key ? null : key));
    // Lazy-load matching alerts on first expand of a row with sprayed tenants.
    if (expandedKey !== key && !matchCache[key]) {
      const tenantIds = (row.sprayedTenants || []).map((t) => t.tenantId).filter(Boolean);
      if (tenantIds.length === 0) {
        setMatchCache((prev) => ({ ...prev, [key]: { loading: false, error: null, data: { alerts: [], scannedTenants: 0 } } }));
        return;
      }
      setMatchCache((prev) => ({ ...prev, [key]: { loading: true, error: null, data: null } }));
      fetchSprayOriginMatchingAlerts(row.prefixCidr, tenantIds)
        .then((data) => setMatchCache((prev) => ({ ...prev, [key]: { loading: false, error: null, data } })))
        .catch((err) => setMatchCache((prev) => ({ ...prev, [key]: { loading: false, error: err.message || 'Failed to load alerts', data: null } })));
    }
  };

  const openRevoke = (row) => { setRevokeTarget(row); setRevokeReason(''); };
  const closeRevoke = () => { setRevokeTarget(null); setRevokeReason(''); };

  const submitRevoke = async () => {
    if (!revokeTarget || !revokeReason.trim()) return;
    setSubmitting(true);
    try {
      await updateSprayOriginWatchlistEntry({
        rowKey: revokeTarget.rowKey,
        enabled: false,
        disableReason: revokeReason.trim(),
        prefixCidr: revokeTarget.prefixCidr,
      });
      setRows((prev) => prev.map((x) => (x.rowKey === revokeTarget.rowKey ? {
        ...x, enabled: false, disabledBy: user?.username || user?.name || '', disabledUtc: new Date().toISOString(), disableReason: revokeReason.trim(),
      } : x)));
      closeRevoke();
      loadHealth();
    } catch (err) {
      setError(err.message || 'Failed to revoke entry');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReenable = async (row) => {
    setTogglingKey(row.rowKey);
    try {
      await updateSprayOriginWatchlistEntry({ rowKey: row.rowKey, enabled: true, prefixCidr: row.prefixCidr });
      setRows((prev) => prev.map((x) => (x.rowKey === row.rowKey ? { ...x, enabled: true } : x)));
      loadHealth();
    } catch (err) {
      setError(err.message || 'Failed to re-enable entry');
    } finally {
      setTogglingKey(null);
    }
  };

  const handleReconcile = async () => {
    setReconciling(true);
    try {
      await reconcileSprayOriginWatchlistSentinel();
      setConfirmReconcile(false);
      loadHealth();
    } catch (err) {
      setError(err.message || 'Failed to reconcile sentinel');
    } finally {
      setReconciling(false);
    }
  };

  const cols = [
    { label: '',            sortKey: null },
    { label: 'Prefix',      sortKey: 'prefixCidr' },
    { label: 'Ver',         sortKey: 'ipVersion' },
    { label: 'Tenants',     sortKey: 'sprayedTenantCount' },
    { label: 'Sprays',      sortKey: 'sprayCount' },
    { label: 'First seen',  sortKey: 'firstSeenUtc' },
    { label: 'Last seen',   sortKey: 'lastSeenUtc' },
    { label: 'Risk',        sortKey: 'riskLevel' },
    { label: 'Enabled',     sortKey: 'enabled' },
  ];

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <SignalIcon className="h-7 w-7 text-rose-400" />
          <h1 className="text-2xl font-bold text-gray-100">Spray-Origin IP Watchlist</h1>
        </div>
        <p className="text-sm text-gray-500 max-w-3xl">
          Attacker originating IP prefixes auto-captured from Entra ID Protection <strong>password-spray</strong>
          alerts. The detection rule <span className="font-mono">rule_spray_success_signin</span> fires
          <strong> HIGH</strong> when a <em>successful</em> sign-in comes from a listed prefix — "the spray
          succeeded." The list is <strong>global</strong> (a prefix that sprayed any tenant protects all of
          them). If a benign shared egress (corporate VPN, CGNAT/carrier range, over-wide IPv6 supernet) gets
          auto-added, <strong>revoke</strong> it here to stop the rule firing on it within ~30&nbsp;s — the row is
          kept for audit (soft-disable, never deleted). Entries are pipeline-written; you can't add one here.
        </p>
      </div>

      {health?.outboxPending && (
        <div className="mb-4 p-3 bg-amber-900/30 border border-amber-700/50 rounded-lg text-amber-300 text-sm flex items-center gap-2">
          <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
          <span>
            Pending sync since {formatDate(health.outboxEnqueuedUtc)} ({health.outboxAttemptCount} attempt{health.outboxAttemptCount !== 1 ? 's' : ''}).
            The detection pipeline may not yet see recent changes. The retry timer runs every ~30&nbsp;s.
          </span>
          {isAdmin && (
            <button onClick={() => setConfirmReconcile(true)} className="ml-auto underline hover:no-underline text-amber-200">
              Reconcile now
            </button>
          )}
        </div>
      )}

      {confirmReconcile && (
        <div className="mb-4 p-3 bg-gray-900 border border-gray-700 rounded-lg text-sm flex items-center gap-3">
          <ArrowsRightLeftIcon className="h-4 w-4 text-blue-400 flex-shrink-0" />
          <span className="text-gray-300">Force-bump the sentinel? This recomputes the version monotonically and clears any pending outbox row.</span>
          <button onClick={handleReconcile} disabled={reconciling} className="btn-primary px-3 py-1 text-xs ml-auto">
            {reconciling ? 'Reconciling…' : 'Confirm'}
          </button>
          <button onClick={() => setConfirmReconcile(false)} className="btn-secondary px-3 py-1 text-xs">Cancel</button>
        </div>
      )}

      {!isAdmin && (
        <div className="card mb-6 flex items-center gap-3 text-sm text-gray-400">
          <LockClosedIcon className="h-4 w-4 text-gray-500" />
          <span>Read-only view — admin access is required to revoke or re-enable entries.</span>
        </div>
      )}

      {error && (
        <div className="mb-4 p-4 bg-red-900/30 border border-red-700/50 rounded-lg text-red-300 text-sm flex items-center gap-2">
          <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={load} className="ml-auto underline hover:no-underline text-red-400">Retry</button>
        </div>
      )}

      <RefreshingBanner show={loading && hasLoaded} message="Refreshing watchlist…" />

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div className="flex flex-col sm:flex-row gap-3 flex-1">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
            <input
              type="text"
              placeholder="Filter by prefix, exact IP, tenant, source…"
              value={filterQuery}
              onChange={(e) => setFilterQuery(e.target.value)}
              className="input-field pl-9"
            />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="input-field w-40">
            <option value="all">All entries</option>
            <option value="enabled">Enabled only</option>
            <option value="disabled">Revoked only</option>
          </select>
          <input
            type="number"
            min="1"
            placeholder="Min tenants"
            value={minTenants}
            onChange={(e) => setMinTenants(e.target.value)}
            className="input-field w-32"
            title="Show prefixes that sprayed at least this many tenants"
          />
        </div>
        <div className="flex items-center gap-3 text-xs text-gray-500">
          {isAdmin && (
            <button onClick={() => setConfirmReconcile(true)} disabled={reconciling} className="btn-secondary flex items-center gap-1.5 text-xs" title="Force-bump the sentinel (recovery)">
              <ArrowsRightLeftIcon className="h-3.5 w-3.5" />
              Reconcile sentinel
            </button>
          )}
          <RefreshButton loading={loading} onClick={load} />
          <span>{filtered.length} entr{filtered.length !== 1 ? 'ies' : 'y'}</span>
        </div>
      </div>

      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table ref={tableRef} className="text-sm" style={{ tableLayout: 'fixed', width: `${colWidths.reduce((a, b) => a + b, 0)}px` }}>
            <thead>
              <tr className="border-b border-gray-800">
                {cols.map((col, colIdx) => (
                  <th
                    key={colIdx}
                    ref={(el) => { thRefs.current[colIdx] = el; }}
                    onClick={col.sortKey ? () => toggleSort(col.sortKey) : undefined}
                    className={`text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3 whitespace-nowrap relative select-none ${col.sortKey ? 'cursor-pointer hover:text-gray-300' : ''}`}
                    style={{ width: `${colWidths[colIdx]}px` }}
                  >
                    <span className="truncate">{col.label}</span>
                    {col.sortKey && <SortIndicator k={col.sortKey} />}
                    <div className="absolute top-0 right-0 h-full w-2 cursor-col-resize z-10 flex items-stretch justify-center group" onMouseDown={(e) => { e.stopPropagation(); startResize(e, colIdx); }}>
                      <div className="w-px bg-gray-700/50 group-hover:bg-blue-500/70 transition-colors" />
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/50">
              {loading && !hasLoaded && (
                <tr>
                  <td colSpan={cols.length} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <svg className="animate-spin h-8 w-8 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      <span className="text-gray-500 text-sm">Loading watchlist…</span>
                    </div>
                  </td>
                </tr>
              )}

              {hasLoaded && filtered.length === 0 && (
                <tr>
                  <td colSpan={cols.length} className="px-6 py-12 text-center">
                    <SignalIcon className="h-10 w-10 text-gray-700 mx-auto mb-3" />
                    <p className="text-gray-400 font-medium">No spray-origin prefixes</p>
                    <p className="text-gray-600 text-sm mt-1">
                      {filterQuery || statusFilter !== 'all' || minTenants
                        ? 'Try adjusting your filters.'
                        : 'The watchlist fills as password-spray alerts land — it can take hours to days for the first entries to accrue.'}
                    </p>
                  </td>
                </tr>
              )}

              {paged.map((r) => {
                const isEnabled = r.enabled !== false;
                const isExpanded = expandedKey === r.rowKey;
                const tenantNames = (r.sprayedTenants || [])
                  .map((t) => t.friendlyName || t.tenantId)
                  .filter(Boolean);
                const tenantPreview = tenantNames.slice(0, 2).join(', ');
                const moreCount = Math.max(0, tenantNames.length - 2);
                return (
                  <React.Fragment key={r.rowKey}>
                    <tr className={`hover:bg-gray-800/50 transition-colors cursor-pointer ${isEnabled ? '' : 'opacity-60'}`} onClick={() => toggleExpand(r)}>
                      <td className="px-3 py-3 text-gray-500">
                        {isExpanded ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />}
                      </td>
                      <td className="px-4 py-3 truncate overflow-hidden" title={r.prefixCidr}>
                        <span className="font-mono text-xs text-gray-200">{r.prefixCidr || r.rowKey}</span>
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-xs">{ipVersionLabel(r.ipVersion)}</td>
                      <td className="px-4 py-3 truncate overflow-hidden" title={tenantNames.join(', ')}>
                        <span className="text-gray-300">{r.sprayedTenantCount ?? 0}</span>
                        {tenantPreview && (
                          <span className="text-gray-500 text-xs ml-2">({tenantPreview}{moreCount > 0 ? `, +${moreCount}` : ''})</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-300 text-xs">{r.sprayCount ?? '—'}</td>
                      <td className="px-4 py-3 text-gray-500 text-xs truncate overflow-hidden">{formatDate(r.firstSeenUtc)}</td>
                      <td className="px-4 py-3 text-gray-400 text-xs truncate overflow-hidden">{formatDate(r.lastSeenUtc)}</td>
                      <td className="px-4 py-3">
                        {r.riskLevel ? <Badge tone={severityTone(r.riskLevel)}>{r.riskLevel}</Badge> : <span className="text-gray-600">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        <Badge tone={isEnabled ? 'success' : 'neutral'}>{isEnabled ? 'Enabled' : 'Revoked'}</Badge>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="bg-gray-900/40">
                        <td colSpan={cols.length} className="px-6 py-5">
                          <DetailPanel
                            row={r}
                            isAdmin={isAdmin}
                            isEnabled={isEnabled}
                            matchState={matchCache[r.rowKey]}
                            togglingKey={togglingKey}
                            onRevoke={() => openRevoke(r)}
                            onReenable={() => handleReenable(r)}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        {hasLoaded && filtered.length > 0 && (
          <Pagination
            currentPage={safeCurrentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            totalItems={filtered.length}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
          />
        )}
      </div>

      {revokeTarget && (
        <RevokeModal
          row={revokeTarget}
          reason={revokeReason}
          setReason={setRevokeReason}
          submitting={submitting}
          onConfirm={submitRevoke}
          onCancel={closeRevoke}
        />
      )}
    </div>
  );
}

// ── Expanded-row detail / provenance + matching alerts ────────────────────────
function DetailPanel({ row, isAdmin, isEnabled, matchState, togglingKey, onRevoke, onReenable }) {
  const exactIps = (row.exactIpsSeen || '').split(/[,\s]+/).filter(Boolean);
  const targetUpns = (row.targetUpnsSample || '').split(/[,\s]+/).filter(Boolean);
  const alerts = matchState?.data?.alerts || [];
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="space-y-3">
          <Field label="Prefix (CIDR)"><span className="font-mono text-gray-200">{row.prefixCidr || '—'}</span></Field>
          <Field label="Source feed"><span className="text-gray-300">{row.sourceFeed || '—'}</span></Field>
          <Field label="Spray window">
            <span className="text-gray-300">{formatDateTime(row.firstSeenUtc)} → {formatDateTime(row.lastSeenUtc)}</span>
          </Field>
          <Field label="TTL expiry (last seen + 14d)">
            <span className="text-gray-400">{formatDateTime(row.ttlExpiryUtc)}</span>
          </Field>
          {!isEnabled && (
            <Field label="Revoked">
              <span className="text-gray-300">{formatDateTime(row.disabledUtc)} by {row.disabledBy || '—'}</span>
              {row.disableReason && <span className="block text-amber-300/90 mt-1">“{row.disableReason}”</span>}
            </Field>
          )}
        </div>
        <div className="space-y-3">
          <Field label={`Tenants sprayed (${row.sprayedTenantCount ?? 0})`}>
            {(row.sprayedTenants || []).length === 0 ? (
              <span className="text-gray-500">—</span>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {row.sprayedTenants.map((t) => (
                  <span key={t.tenantId} className="px-2 py-0.5 rounded bg-gray-800 border border-gray-700 text-xs text-gray-300" title={t.tenantId}>
                    {t.friendlyName || t.tenantId}
                  </span>
                ))}
              </div>
            )}
          </Field>
          <Field label={`Exact IPs seen (sample, ${exactIps.length})`}>
            {exactIps.length === 0 ? <span className="text-gray-500">—</span> : (
              <div className="flex flex-wrap gap-1.5">
                {exactIps.map((ip) => <span key={ip} className="font-mono text-xs text-gray-400">{ip}</span>)}
              </div>
            )}
          </Field>
          {targetUpns.length > 0 && (
            <Field label={`Target UPNs sprayed (sample, ${targetUpns.length})`}>
              <div className="flex flex-wrap gap-1.5">
                {targetUpns.map((u) => <span key={u} className="text-xs text-gray-400">{u}</span>)}
              </div>
            </Field>
          )}
        </div>
      </div>

      {/* Matching alerts cross-link */}
      <div>
        <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
          Alerts that matched this prefix
        </h4>
        {matchState?.loading && <p className="text-sm text-gray-500">Searching sprayed tenants…</p>}
        {matchState?.error && <p className="text-sm text-red-400">{matchState.error}</p>}
        {matchState && !matchState.loading && !matchState.error && (
          alerts.length === 0 ? (
            <p className="text-sm text-gray-500">
              No fired <span className="font-mono">rule_spray_success_signin</span> alerts matched this prefix
              {matchState.data?.scannedTenants != null ? ` (scanned ${matchState.data.scannedTenants} tenant${matchState.data.scannedTenants !== 1 ? 's' : ''})` : ''}.
              {' '}A clean record here strengthens the case that this is a benign false positive.
            </p>
          ) : (
            <div className="space-y-1.5">
              {matchState.data?.truncated && (
                <p className="text-xs text-amber-300/80">Results truncated — showing the most recent matches.</p>
              )}
              {alerts.map((a) => (
                <Link
                  key={`${a.tenantId}/${a.alertId}`}
                  to={`/alerts/${a.tenantId}/${a.alertId}`}
                  className="flex items-center gap-3 text-sm bg-gray-800/60 hover:bg-gray-800 border border-gray-700/60 rounded px-3 py-2 transition-colors"
                >
                  <Badge tone={severityTone(a.severity)}>{a.severity || 'high'}</Badge>
                  <span className="text-gray-300">{a.friendlyName || a.tenantId}</span>
                  <span className="text-gray-400 text-xs">{a.userId || '—'}</span>
                  <span className="font-mono text-gray-500 text-xs">{a.clientIp || ''}</span>
                  <span className="text-gray-600 text-xs ml-auto">{formatDateTime(a.alertTime || a.eventTime)}</span>
                  <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 text-gray-500" />
                </Link>
              ))}
            </div>
          )
        )}
      </div>

      {/* Actions */}
      {isAdmin && (
        <div className="flex items-center gap-3 pt-1">
          {isEnabled ? (
            <button onClick={onRevoke} className="btn-danger flex items-center gap-2 text-sm">
              <NoSymbolIcon className="h-4 w-4" />
              Revoke (disable)
            </button>
          ) : (
            <button onClick={onReenable} disabled={togglingKey === row.rowKey} className="btn-secondary flex items-center gap-2 text-sm">
              <ArrowUturnLeftIcon className="h-4 w-4" />
              {togglingKey === row.rowKey ? 'Re-enabling…' : 'Re-enable'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-0.5">{label}</div>
      <div className="text-sm">{children}</div>
    </div>
  );
}

// ── Revoke confirmation modal (required free-text reason) ─────────────────────
function RevokeModal({ row, reason, setReason, submitting, onConfirm, onCancel }) {
  const canConfirm = reason.trim().length > 0;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onCancel}>
      <div className="card max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-3">
          <NoSymbolIcon className="h-5 w-5 text-rose-400" />
          <h3 className="text-lg font-semibold text-gray-100">Revoke spray-origin prefix</h3>
        </div>
        <p className="text-sm text-gray-400 mb-4">
          Revoke this spray-origin prefix? <span className="font-mono text-gray-300">rule_spray_success_signin</span> will
          stop firing on sign-ins from <span className="font-mono text-gray-200">{row.prefixCidr}</span> across all
          tenants within ~30&nbsp;s. Alerts already raised are not withdrawn.
        </p>
        <label className="block text-xs text-gray-500 mb-1">Reason (required — why is this a false positive?)</label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          autoFocus
          placeholder="e.g. Zscaler shared egress for Contoso — not attacker-controlled"
          className="input-field w-full text-sm"
        />
        {!canConfirm && <p className="text-xs text-amber-300/80 mt-1">A reason is required to revoke.</p>}
        <div className="flex items-center justify-end gap-3 mt-4">
          <button onClick={onCancel} className="btn-secondary px-4 py-2 text-sm">Cancel</button>
          <button onClick={onConfirm} disabled={!canConfirm || submitting} className="btn-danger px-4 py-2 text-sm">
            {submitting ? 'Revoking…' : 'Revoke prefix'}
          </button>
        </div>
      </div>
    </div>
  );
}
